import React, { PropTypes } from "react";
import ReactDOM from "react-dom";
import { Table } from "fixed-data-table";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import ReactTimeout from "react-timeout";

import { RectangleSpinner } from "../spinners";
import { createColumn, TableQ, TablePages } from "./";
import RefreshButton from "../RefreshButton";

const MINIMUM_QUERY_LENGTH = 3;

class DataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      fetching: false,
      q: "",
      tableWidth: 400,
      ready: false,
    };
    this.setQ = this.setQ.bind(this);
    this.sendQ = _.debounce(this.sendQ.bind(this), 250);
    this.setOffset = this.setOffset.bind(this);
    this.setSort = this.setSort.bind(this);
    this.throttle = this.throttle.bind(this);
    this._update = this._update.bind(this);
    this.reloadData = this.reloadData.bind(this);
    this.getRefreshButton = this.getRefreshButton.bind(this);
    this.showPlaceHolderMessage = this.showPlaceHolderMessage.bind(this);
  }

  componentDidMount() {
    const { getData, filters, rowsPerPage, sortBy } = this.props;

    let win = window;
    if (win.addEventListener) {
      win.addEventListener("resize", this.throttle, false);
      win.addEventListener("scroll", this.throttle, false);
    } else if (win.attachEvent) {
      win.attachEvent("onresize", this.throttle);
      win.attachEvent("onscroll", this.throttle);
    } else {
      win.onresize = this._update.bind(this);
      win.onscroll = this._update.bind(this);
    }
    this._update(this);
    this.setState({ ready: true }, () =>
      getData(0, rowsPerPage, this.state.q, sortBy, filters)
    );
  }

  componentWillReceiveProps(nextProps) {
    this._update(this, nextProps);
  }

  componentWillUpdate() {
    this.props.setTimeout(() => {
      const windowWidth = window.innerWidth > 1024 ? window.innerWidth : 1024;
      const titleWidth = document.getElementsByClassName(
        "datatable-actions-section"
      )[0]
        ? windowWidth -
          document.getElementsByClassName("datatable-actions-section")[0]
            .offsetWidth -
          80 +
          "px"
        : "";
      if (titleWidth !== this.state.titleWidth) {
        this.setState({ titleWidth });
      }
    }, 0);
  }

  componentWillUnmount() {
    let win = window;
    if (win.removeEventListener) {
      win.removeEventListener("resize", this.throttle, false);
      win.removeEventListener("scroll", this.throttle, false);
    } else if (win.detachEvent) {
      win.detachEvent("onresize", this.throttle);
      win.detachEvent("onscroll", this.throttle);
    } else {
      win.onresize = null;
      win.onscroll = null;
    }
  }

  throttle() {
    _.throttle(this._update.bind(this, this), 0)();
  }

  _update(node) {
    node = ReactDOM.findDOMNode(node);

    this.setState({
      tableWidth: node.clientWidth,
    });
  }

  sendQ(q) {
    const { getData, filters, rowsPerPage, sortBy } = this.props;

    if (typeof q !== 'string') {
      return;
    }

    if (q.length == 0 || q.length >= MINIMUM_QUERY_LENGTH) {
      const promise = getData(0, rowsPerPage, q, sortBy, filters);

      if (promise)
        promise.then(() => {
          this.setState({ fetching: false });
        });
      else this.setState({ fetching: false });
    }
  }

  setQ(q) {
    this.setState({ q, fetching: true });
    this.sendQ(q);
  }

  setOffset(offset) {
    const { getData, filters, rowsPerPage, sortBy } = this.props;
    getData(offset, rowsPerPage, this.state.q, sortBy, filters);
  }

  setSort(sortBy) {
    const { getData, filters, rowsPerPage } = this.props;
    getData(0, rowsPerPage, this.state.q, sortBy, filters);
  }

  reloadData() {
    this.setState({ fetching: true });
    const { refreshFunction, filters, rowsPerPage } = this.props;
    refreshFunction(0, rowsPerPage, this.state.q, this.props.sortBy, filters).then(() => {this.setState({ fetching: false });});
  }

  getRefreshButton(disable) {
    const { fetching } = this.state;
    return (
      <div className="datatable-header">
        <div className="datatable-button-container">
          <RefreshButton
            onClick={this.reloadData}
            disabled={fetching || disable}
            label={<FormattedMessage id="datatable.refreshData"/>}
          />
        </div>
      </div>
    );
  }

  showPlaceHolderMessage(notLoaded, data) {
    if(this.props.showLoadingMessage) {
      return (
          <div className="empty-datatable">
            <FormattedMessage id="common.loading" />
        </div>
      );
    }
    if(notLoaded) {
      return <RectangleSpinner/>;
    }
    if(data && data.length === 0) {
      return (
          <div className="empty-datatable">
            <FormattedMessage id="datatable.noData" />
          </div>
      );
    }
  }

  render() {
    const {
      addButton,
      cols,
      data: rawData,
      dataFilter,
      filterButtons,
      offset,
      rowHeight,
      rowsPerPage,
      showFilters,
      showSelectedCheckboxes,
      showOverflow,
      showTitle,
      sortBy,
      title,
      totalRows,
      showEmptyFiltersBtn,
      customEmptyBtnFilterClass,
      clearDataTableFilters,
      hideSearch,
      refreshFunction,
    } = this.props;

    const { fetching, tableWidth, q } = this.state;

    if (!this.state.ready) {
      return <div />;
    }
    const headerHeight = 42;
    const dataTableClassNames = classNames({
      datatable: true,
      "show-overflow": showOverflow,
    });
    const data = dataFilter ? dataFilter(rawData) : rawData;
    const dataLength = data ? data.length : 0;
    
    const columns = cols.map((col) =>
      createColumn(data, col, sortBy, this.setSort, tableWidth)
    );
    let mapping = {};

    mapping.showSelectedCheckboxes = showSelectedCheckboxes;

    const notLoaded = data == null && !totalRows;

    return (
      <div className={dataTableClassNames}>
        {showTitle && (
          <div className="datatable-header">
            <div className="datatable-title-container">
              <h2 className="datatable-title">{title}</h2>
            </div>
          </div>
        )}
        {refreshFunction != undefined && this.getRefreshButton(notLoaded)}
        {showFilters && (
          <div className="datatable-actions-section">
            {filterButtons}
            {(q !== "" || totalRows || fetching) && !hideSearch ? (
              <TableQ setQ={this.setQ} q={q} />
            ) : null}
            <div className="dataTable-add-button">
              {addButton}
              {showEmptyFiltersBtn && (
                    <div>
                      <br/>
                      <button
                          className={`btn btn-primary empty-client-filters ${customEmptyBtnFilterClass}`}
                          type="button"
                          onClick={clearDataTableFilters}
                      >
                          <FormattedMessage id="dtv2ClearFilter" />
                      </button>
                    </div>
                )
              }
            </div>
          </div>
        )}
        {showSelectedCheckboxes != 0 && (
          <FormattedMessage id="common.showSelectedCheckboxes" values={mapping}>
            {(m) => (
              <div
                className="formatted-confirmation"
                dangerouslySetInnerHTML={{ __html: m }}
              />
            )}
          </FormattedMessage>
        )}
        {this.showPlaceHolderMessage(notLoaded, data) || (
            <div>
              <Table
                rowsCount={dataLength}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                width={tableWidth}
                height={dataLength * rowHeight + headerHeight + dataLength}
              >
                {columns}
              </Table>
              {data == null && <RectangleSpinner />}
            </div>
          )}
        <TablePages
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          offset={offset}
          setOffset={this.setOffset}
        />
      </div>
    );
  }
}

DataTable.propTypes = {
  addButton: PropTypes.node,
  cols: PropTypes.array.isRequired,
  containerWidth: PropTypes.number,
  data: PropTypes.array,
  dataFilter: PropTypes.func,
  getData: PropTypes.func.isRequired,
  filterButtons: PropTypes.node,
  filters: PropTypes.array,
  offset: PropTypes.number.isRequired,
  setTimeout: PropTypes.func,
  showFilters: PropTypes.bool,
  showSelectedCheckboxes: PropTypes.number.isRequired,
  showOverflow: PropTypes.bool,
  showTitle: PropTypes.bool,
  sortBy: PropTypes.array,
  title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  totalRows: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number,
  rowHeight: PropTypes.number,
  showEmptyFiltersBtn: PropTypes.bool,
  resetFiltersState: PropTypes.func,
  customEmptyBtnFilterClass: PropTypes.string,
  clearDataTableFilters: PropTypes.func,
  hideSearch: PropTypes.bool,
  refreshFunction: PropTypes.func,
  showLoadingMessage: PropTypes.bool
};

DataTable.defaultProps = {
  showFilters: true,
  showSelectedCheckboxes: 0,
  showOverflow: true,
  showTitle: false,
  rowHeight: 42,
  hideSearch: false
};

export default ReactTimeout(DataTable);



// WEBPACK FOOTER //
// ./src/components/common/DataTable/DataTable.js